'use client';

import type { GiftOrderProductReviewDto } from '__generated__/api/unauth';
import SvgChevrondown from '@common/__generated__/icon/SvgChevrondown';
import { Button, Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { AuthInfiniteQueryOptions, AuthMutation, UnauthInfiniteQueryOptions, UnauthQueryOptions } from '@/api';
import { ReviewCard } from '@/app/donation/_components/ReviewCard';
import { ReviewCardMenu } from '@/app/donation/_components/ReviewCardMenu';
import type { GiftReviewSortType } from '@/app/donation/types';
import { LoadingPageTemplate } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';
import { openResponsiveDialog } from '@/stores/DrawerStore';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';
import { REVIEW_DETAIL_CONTENT_QUERY_KEY, ReviewDetailContent } from '../../../../ReviewDetailContent';
const INITIAL_PAGE = 1;
const INITIAL_SIZE = 20;
interface ReviewListProps {
  initialList: GiftOrderProductReviewDto[];
  totalCount: number;
  sortType?: GiftReviewSortType;
  productNumber: string;
}
export const ReviewList = ({
  initialList,
  totalCount,
  sortType,
  productNumber
}: ReviewListProps) => {
  const router = useCustomRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const {
    toast,
    toastError
  } = useToast();
  const isLogin = useAuthStore(state => state.isLogin);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const openReviewDetailDialog = (reviewId: number, isPhotoReview?: boolean) => {
    openResponsiveDialog({
      component: ReviewDetailContent,
      modalHeader: isPhotoReview ? '포토후기' : '후기',
      className: {
        modal: 'max-w-[900px]'
      },
      props: {
        reviewId
      },
      drawerNoPadding: true
    });
  };
  const handleDetailReviewId = async (reviewId_0: number) => {
    setIsReviewLoading(true);
    try {
      //* 리뷰 상세 데이터를 먼저 조회하여 포토 후기 여부를 판단합니다.
      const queryOptions = UnauthQueryOptions[REVIEW_DETAIL_CONTENT_QUERY_KEY]({
        path: {
          reviewId: reviewId_0
        }
      });
      const data = await queryClient.fetchQuery({
        ...queryOptions
      });

      //* 파일이 하나 이상 있으면 포토후기로 간주합니다.
      const isPhotoReview_0 = !!data?.context?.fileUrlList && data.context.fileUrlList.length > 0;
      openReviewDetailDialog(reviewId_0, isPhotoReview_0);
    } catch {
      //* 실패 시에도 기본 헤더로 모달은 열어줍니다.
      openReviewDetailDialog(reviewId_0);
    } finally {
      setIsReviewLoading(false);
    }
  };
  const {
    data: data_0,
    fetchNextPage,
    hasNextPage,
    isFetching
  } = useInfiniteQuery({
    ...filledInfiniteQueryOptions(isLogin ? AuthInfiniteQueryOptions['get:/wello/v2/auth/home/gift-review']({
      query: {
        productNumber,
        sortType
      }
    }) : UnauthInfiniteQueryOptions['get:/wello/v2/allows/home/gift-review']({
      query: {
        productNumber,
        sortType
      }
    })),
    placeholderData: {
      pageParams: [INITIAL_PAGE],
      pages: [{
        context: {
          contents: initialList,
          totalCount: totalCount,
          page: INITIAL_PAGE,
          size: INITIAL_SIZE
        }
      }]
    }
  });
  const {
    mutate: deleteReview
  } = useMutation({
    ...AuthMutation['delete:/wello/v2/auth/home/gift-review/{reviewId}'](),
    onSuccess: () => {
      toast({
        message: '리뷰가 삭제되었습니다.'
      });
      queryClient.invalidateQueries();
    },
    onError: error => {
      toastError({
        error
      });
    }
  });
  const moveToEditForm = (reviewId_1: number) => {
    if (!pathname) {
      return;
    }
    router.replace(DONATION_ROUTES.MY_REVIEW_EDIT.withSearchParams({
      dynamicPath: {
        reviewId: reviewId_1
      },
      searchParams: {
        redirect: pathname + window.location.search
      }
    }));
  };
  const reviewList = data_0?.pages.flatMap(page => page.context?.contents ?? []) ?? [];
  return <>
      {isReviewLoading ? <LoadingPageTemplate blur fadeIn /> : null}
      <ul className="flex flex-col">
        {reviewList.map(({
        reviewId: reviewId_2,
        contents,
        createdAt: created_at,
        rating,
        productName: productName,
        fileUrlList: file_url_list,
        nickname,
        mineYN: mine_yn,
        answer,
        answerAt: answer_at
      }) => <li key={reviewId_2}>
              <button type="button" onClick={() => handleDetailReviewId(reviewId_2)} className="w-full">
                <ReviewCard rating={rating} createdAt={created_at} productName={productName} contents={contents} imageUrlList={file_url_list} reviewer={nickname} answer={answer} answerAt={answer_at} actions={mine_yn ? <ReviewCardMenu onDelete={() => deleteReview({
            path: {
              reviewId: reviewId_2
            }
          })} onEdit={() => moveToEditForm(reviewId_2)} /> : undefined} />
              </button>
            </li>)}

        {hasNextPage ? <li className="my-30 w-full px-20 flex-center">
            <Button theme="sub-line" dimensions="H48" onClick={() => fetchNextPage()} className="w-full gap-6">
              더보기 <SvgChevrondown className="size-16 text-Gray-600" />
            </Button>
          </li> : null}
        {isFetching && <li className="my-60 w-full flex-center">
            <Spinner />
          </li>}
      </ul>
    </>;
};